import { useEffect, useLayoutEffect, useRef, useState, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { useQuery } from '@tanstack/react-query'
import { Search, Hash, Folder } from 'lucide-react'
import { tasksService } from '../../../services/tasksService'
import { SpaceGlyph } from '../spaceIcons'

// Liste seçici — space→folder→list düz ağaç, arama. "Listeye taşı" için.
export default function ListPickerPopover({ company, x, y, excludeListId, onPick, onClose }) {
  const ref = useRef(null)
  const [q, setQ] = useState('')
  const [pos, setPos] = useState({ left: x, top: y })
  const { data } = useQuery({ queryKey: ['task-spaces', company, 'active'], queryFn: () => tasksService.listSpaces(company, 'active'), staleTime: 60000 })
  const spaces = data?.data?.spaces || []

  useLayoutEffect(() => {
    const el = ref.current; if (!el) return
    const r = el.getBoundingClientRect()
    let left = x, top = y
    if (left + r.width > window.innerWidth - 8) left = Math.max(8, window.innerWidth - r.width - 8)
    if (top + r.height > window.innerHeight - 8) top = Math.max(8, window.innerHeight - r.height - 8)
    setPos({ left, top })
  }, [x, y])

  useEffect(() => {
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose?.() }
    const onKey = (e) => { if (e.key === 'Escape') onClose?.() }
    document.addEventListener('mousedown', onDown, true)
    document.addEventListener('keydown', onKey)
    return () => { document.removeEventListener('mousedown', onDown, true); document.removeEventListener('keydown', onKey) }
  }, [onClose])

  // Düz liste: {list, spaceName, folderName}
  const flat = useMemo(() => {
    const out = []
    const walkFolder = (sp, f) => {
      ;(f.lists || []).forEach((l) => out.push({ list: l, space: sp, folderName: f.name }))
      ;(f.folders || []).forEach((sf) => walkFolder(sp, sf))
    }
    spaces.forEach((sp) => {
      ;(sp.lists || []).forEach((l) => out.push({ list: l, space: sp, folderName: null }))
      ;(sp.folders || []).forEach((f) => walkFolder(sp, f))
    })
    const t = q.trim().toLocaleLowerCase('tr')
    return out.filter((r) => r.list.id !== excludeListId && (!t || r.list.name.toLocaleLowerCase('tr').includes(t) || (r.space.name || '').toLocaleLowerCase('tr').includes(t)))
  }, [spaces, q, excludeListId])

  return createPortal(
    <div ref={ref} style={{ left: pos.left, top: pos.top, width: 300 }}
      onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}
      className="fixed z-[80] bg-white rounded-xl shadow-2xl border border-gray-100 py-2">
      <div className="px-2 pb-2">
        <div className="relative">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Liste ara…"
            className="w-full pl-7 pr-2 py-1.5 text-sm border border-gray-200 rounded-lg outline-none focus:ring-1 focus:ring-violet-200" />
        </div>
      </div>
      <div className="max-h-[300px] overflow-y-auto px-1">
        {flat.map((r) => (
          <button key={r.list.id} onClick={() => onPick?.(r.list.id)}
            className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-left hover:bg-gray-50">
            <SpaceGlyph icon={r.space.icon} color={r.space.color} name={r.space.name} size={10} />
            <Hash size={11} className="text-gray-400 shrink-0" style={{ color: r.list.color || undefined }} />
            <span className="text-[13px] text-gray-800 truncate">{r.list.name}</span>
            <span className="ml-auto text-[10px] text-gray-400 truncate max-w-[90px] flex items-center gap-0.5">
              {r.folderName && <Folder size={9} className="text-amber-500" />}{r.folderName || r.space.name}
            </span>
          </button>
        ))}
        {flat.length === 0 && <div className="py-4 text-center text-xs text-gray-400">liste yok</div>}
      </div>
    </div>,
    document.body,
  )
}
